import { useEffect } from 'react';
import { getClients, getForceClients } from './utils/url';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addClients, SubmitAddClients } from './components/Auth/api/addClients';
import { Aside } from './components/Aside';
import { Outlet } from 'react-router-dom';
import {
  getPsychologist,
  ResponseGetPsychologist,
} from './api/getPsychologist';
import { useUserStore } from './store/user';
import ToastProvider from './widgets/ToastProvider';
import { Navbar } from './widgets/Navbar';
import { Drawer } from './widgets/Drawer';
import { useLocale } from './hooks/useLocale';
import { SkeletonMain } from './widgets/SkeletonMain';
import store from 'storejs';
import {
  addClientsForce,
  SubmitAddClientsForce,
} from './components/Auth/api/addClientsForce';
import useAppStore from './store/app';
import { useDeviceDetails } from './hooks/useDeviceDetails';
import { getCookies } from './utils/cookie';

function App() {
  const { setClientStrings, updatePsychologist } = useUserStore();
  const { setInstallEvent } = useAppStore();
  const { isStandalone } = useDeviceDetails();

  const queryClient = useQueryClient();

  useLocale();

  const { mutate: mutateClients } = useMutation({
    mutationFn: (data: SubmitAddClients) => addClients(data),
    onSuccess: () => {
      store.remove('clients');

      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const { mutate: mutateClientsForce } = useMutation({
    mutationFn: (data: SubmitAddClientsForce) => addClientsForce(data),
    onSuccess: () => {
      store.remove('forceClients');

      window.location.href = '/';
    },
  });

  const { data, isPending, error } = useQuery<ResponseGetPsychologist>({
    queryKey: ['psychologist'],
    staleTime: 0,
    queryFn: () => getPsychologist(),
  });

  useEffect(() => {
    if (data) {
      updatePsychologist({
        name: data.name,
        email: data.email,
        img: data.img,
      });
    }
  }, [isPending, data, setClientStrings, updatePsychologist]);

  useEffect(() => {
    const clientsURL = getClients();

    if (clientsURL.length !== 0) {
      store.set('clients', clientsURL);
    }

    const forceClients = getForceClients();

    if (forceClients.length !== 0) {
      store.set('forceClients', forceClients);
    }

    // Без cookie авторизации сервер обязан ответить 400 (token required) —
    // запросы бессмысленны. Очередь клиентов остаётся в storejs: после
    // логина App монтируется заново и попытка повторится уже с токеном.
    if (!getCookies('auth')) {
      return;
    }

    if (store.has('clients')) {
      mutateClients({ clients: store.get('clients') });
    }

    if (store.has('forceClients')) {
      mutateClientsForce({ clients: store.get('forceClients') });
    }
  }, [mutateClients, mutateClientsForce]);

  useEffect(() => {
    if (isStandalone) {
      (window as any).ym(98958356, 'reachGoal', 'install');
    }
  }, [isStandalone]);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', event => {
      event.preventDefault();

      setInstallEvent(event);
    });
  }, [setInstallEvent]);

  if (isPending || error) {
    return <SkeletonMain />;
  }

  return (
    <>
      <ToastProvider />
      <Navbar />
      <Drawer>
        <div className="flex">
          <div className="hidden md:block">
            <Aside />
          </div>

          <Outlet />
        </div>
      </Drawer>
    </>
  );
}

export default App;
